import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { locationKeyFromName } from '../lib/schedule.js';
import { ymdLocal } from '../lib/format.js';

// Shift-level feasibility — Availability Health Level B (CJ 2026-07-29).
// "Can we legally staff this café's template this week, and if not, what's
// unfillable?" Replaces the meaningless 177/300 8-week forecast chart. Quiet at a
// glance (CJ rejected dense viz): one headline line, a short unfillable list, and
// the per-person assignment tucked behind a tap. GET /api/health/feasibility.

const DOW = { sun: 'Sun', mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat' };
function fmtT(hhmm) {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(hhmm || ''));
  if (!m) return hhmm;
  let h = Number(m[1]); const min = m[2];
  const ap = h < 12 ? 'a' : 'p'; h = h % 12 || 12;
  return min === '00' ? `${h}${ap}` : `${h}:${min}${ap}`;
}
const REASON = {
  no_role_match: 'no one on the roster can cover this role',
  no_availability: 'able staff exist, but none are available this window',
  capacity: 'everyone able is already at their weekly limit',
};
// "2 baristas", "1 manager" — pluralize the role for the headline.
function shortPhrase(b) {
  const label = b.role === 'any' ? 'staff' : String(b.role).toLowerCase();
  const plural = b.short === 1 || /s$/.test(label) ? label : label + 's';
  return `${b.short} ${plural}`;
}

export function FeasibilityPanel({ user, location }) {
  const headers = { 'x-user-id': String(user.id) };
  const locKey = location ? locationKeyFromName(location.name) : null;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [offset, setOffset] = useState(0);
  const [showAssign, setShowAssign] = useState(false);

  const mondayOf = (d) => { const x = new Date(d); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x; };
  const baseMonday = mondayOf(new Date());
  const weekMonday = new Date(baseMonday); weekMonday.setDate(baseMonday.getDate() + offset * 7);
  const weekYmd = ymdLocal(weekMonday);

  async function load() {
    if (!locKey) return;
    setLoading(true); setErr('');
    try { setData(await api('GET', `/api/health/feasibility/${locKey}?week=${weekYmd}`, null, headers)); }
    catch (e) { let m = e.message || 'Failed to load'; try { m = JSON.parse(m).error || m; } catch {} setErr(m); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); setShowAssign(false); }, [locKey, offset]);

  const weeks = Array.from({ length: 8 }, (_, i) => {
    const m = new Date(baseMonday); m.setDate(baseMonday.getDate() + i * 7);
    return { i, label: m.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) };
  });

  const shorts = data ? data.byRole.filter((b) => b.short > 0) : [];
  const staffable = data && data.unfilledShifts === 0;
  const noTarget = data && (data.source === 'none' || data.totalShifts === 0);

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700 }}>Can we staff this week?</div>
          <div style={{ fontSize: 11, color: '#757575' }}>
            Legal fill of {data?.source === 'template' ? 'the current template' : data?.source === 'schedule' ? 'the recent schedule' : 'the template'} · ≤5 days / ≤40h / 1 shift a day per person
          </div>
        </div>
        <select value={offset} onChange={(e) => setOffset(Number(e.target.value))}
          style={{ fontSize: 12, padding: '4px 8px', borderRadius: 6, border: '1px solid #d0d4d9', fontFamily: 'inherit' }}>
          {weeks.map((w) => <option key={w.i} value={w.i}>{w.i === 0 ? 'This week' : 'Week of ' + w.label}</option>)}
        </select>
      </div>

      {err && <div style={{ fontSize: 12, color: '#c62828' }}>{err} <button onClick={load} className="btn-secondary" style={{ fontSize: 11, marginLeft: 6 }}>Retry</button></div>}
      {loading && !data && <div style={{ fontSize: 12, color: '#757575' }}>Checking feasibility…</div>}

      {data && (
        <>
          {noTarget ? (
            <div style={{ fontSize: 13, color: '#757575' }}>
              No current template or recent schedule for this café — set one to check staffability.
            </div>
          ) : staffable ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', background: '#e8f5e9', borderRadius: 8 }}>
              <span style={{ fontSize: 18 }}>✅</span>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#2e7d32' }}>Fully staffable</div>
                <div style={{ fontSize: 12, color: '#37474f' }}>All {data.totalShifts} shifts can be legally covered from the available pool.</div>
              </div>
            </div>
          ) : (
            <div style={{ padding: '10px 12px', background: '#fff3e0', borderRadius: 8, borderLeft: '4px solid #ef6c00' }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#e65100' }}>
                ⚠ Short {shorts.map(shortPhrase).join(', ')}
              </div>
              <div style={{ fontSize: 12, color: '#37474f', marginTop: 2 }}>
                {data.unfilledShifts} of {data.totalShifts} shifts can't be legally filled this week.
              </div>
            </div>
          )}

          {/* pool line — the regular vs backup split + who is unconstrained.
              This last part used to warn that a full-fill "may be optimistic"
              because N people hadn't set availability. The solver had already
              treated them as available for every shift, so the warning argued
              with the result beside it. Reframed with the health view
              (CJ 2026-08-06): no reduction rows means no constraints. */}
          {!noTarget && (
            <div style={{ fontSize: 12, color: '#37474f', marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: '2px 12px' }}>
              <span><b>{data.poolSummary.primary}</b> regulars{data.poolSummary.backup ? ` · +${data.poolSummary.backup} backup` : ''}</span>
              {data.poolSummary.openAvailability > 0 && (
                <span style={{ color: '#2e5d42' }}>
                  {data.poolSummary.openAvailability} open availability, no constraints
                  {data.poolSummary.openAvailabilityList?.length
                    ? ` — ${data.poolSummary.openAvailabilityList.map(p => p.name).join(', ')}`
                    : ''}
                </span>
              )}
            </div>
          )}

          {/* unfillable shift list — the specific gaps, grouped by day */}
          {data.unfillable.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#37474f', marginBottom: 4 }}>Unfillable shifts</div>
              {data.unfillable.map((u) => (
                <div key={u.shiftId} style={{ display: 'flex', gap: 8, alignItems: 'baseline', padding: '4px 0', borderBottom: '1px solid #f2f2f2', fontSize: 12 }}>
                  <span style={{ fontWeight: 700, minWidth: 34, color: '#37474f' }}>{DOW[u.dow] || u.dow}</span>
                  <span style={{ minWidth: 96, color: '#37474f' }}>{fmtT(u.start_time)}–{fmtT(u.end_time)}</span>
                  {u.role && <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 999, background: '#eceff1', color: '#455a64' }}>{u.role}</span>}
                  <span style={{ color: '#8a5a1f' }}>{REASON[u.reason] || u.reason}</span>
                </div>
              ))}
            </div>
          )}

          {/* per-person assignment — the draft seed, tucked behind a tap (quiet) */}
          {data.perPerson.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <button onClick={() => setShowAssign((s) => !s)} className="btn-secondary" style={{ fontSize: 11 }}>
                {showAssign ? 'Hide' : 'Show'} the fill ({data.filledShifts} shifts across {data.perPerson.length})
              </button>
              {showAssign && (
                <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {data.perPerson.map((p) => (
                    <span key={p.user_id} title={`${p.days} days · ${p.hours}h`}
                      style={{ fontSize: 11, padding: '3px 8px', borderRadius: 999, background: p.isBackup ? '#eceff1' : '#eef7ef', color: '#37474f', border: '1px solid #e0e0e0' }}>
                      {p.name}{p.isBackup ? ' (backup)' : ''} · {p.days}d/{p.hours}h
                    </span>
                  ))}
                </div>
              )}
              <div style={{ fontSize: 10, color: '#9e9e9e', marginTop: 6 }}>
                A proposed legal fill — the seed for one-click draft scheduling. Not yet a published schedule.
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
